import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { estApi } from "../lib/api";

/** EST-FRS-2 §6 — estimate sanity flags. Blocks nothing; dismiss needs a note (logged). */
export default function SanityFlagsPanel({ estimateId, sanity, readOnly, onChanged }) {
  const [open, setOpen] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const [note, setNote] = useState("");
  const flags = sanity?.flags || [];
  const c = sanity?.counts || { red: 0, amber: 0, dismissed: 0 };
  if (!flags.length) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-1.5 text-[11px] text-emerald-800" data-testid="sanity-clean">
        <ShieldCheck className="h-3.5 w-3.5" /> No sanity flags{c.dismissed ? ` · ${c.dismissed} dismissed` : ""}
      </div>);
  }
  const dismiss = async () => {
    if (note.trim().length < 3) { toast.error("A dismissal needs a note"); return; }
    try {
      const s = await estApi.dismissSanity(estimateId, { rule: dismissing.rule, line_id: dismissing.line_id, section: dismissing.section, note: note.trim() });
      onChanged?.(s); setDismissing(null); setNote(""); toast.success("Flag dismissed — noted in the log");
    } catch (e) { toast.error(e?.response?.data?.detail || "Not dismissed"); }
  };
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/50 text-xs" data-testid="sanity-panel">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-2 text-left" data-testid="sanity-toggle">
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <AlertTriangle className="h-3.5 w-3.5 text-amber-700" />
        <span className="font-semibold text-slate-800">Sanity flags</span>
        <span className="rounded bg-red-100 px-1.5 text-[10px] font-medium text-red-700" data-testid="sanity-red">{c.red} red</span>
        <span className="rounded bg-amber-100 px-1.5 text-[10px] font-medium text-amber-800" data-testid="sanity-amber">{c.amber} amber</span>
        {c.dismissed > 0 && <span className="text-[10px] text-slate-500">{c.dismissed} dismissed</span>}
        <span className="ml-auto text-[10px] text-slate-500">flags block nothing — each shows the line and why</span>
      </button>
      {open && (
        <ul className="max-h-56 overflow-y-auto divide-y divide-amber-100 border-t border-amber-200" data-testid="sanity-list">
          {flags.map((f, i) => (
            <li key={`${f.rule}-${f.line_id}-${f.section}-${i}`} className="flex items-center gap-2 px-3 py-1.5" data-testid={`sanity-flag-${f.rule}`} data-severity={f.severity}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${f.severity === "red" ? "bg-red-500" : "bg-amber-500"}`} />
              <span className="w-56 shrink-0 font-medium text-slate-700">{f.label}</span>
              <span className="flex-1 truncate text-slate-600" title={f.reason}>{f.line ? <span className="text-slate-800">{f.line} — </span> : null}{f.reason}</span>
              {f.section && <span className="shrink-0 rounded bg-white px-1.5 text-[10px] text-slate-500">{f.section}</span>}
              {!readOnly && (
                <button type="button" className="shrink-0 text-slate-400 hover:text-slate-700" title="Dismiss with a note" data-testid={`sanity-dismiss-${f.rule}`}
                        onClick={() => { setDismissing(f); setNote(""); }}><X className="h-3.5 w-3.5" /></button>)}
            </li>))}
        </ul>)}
      <Dialog open={!!dismissing} onOpenChange={(v) => !v && setDismissing(null)}>
        <DialogContent className="max-w-md" data-testid="sanity-dismiss-dialog">
          <DialogHeader><DialogTitle className="text-sm">Dismiss this flag</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-600">{dismissing?.label}: {dismissing?.reason}</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Why is this fine? (required, logged)" data-testid="sanity-dismiss-note" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDismissing(null)} data-testid="sanity-dismiss-cancel">Cancel</Button>
            <Button size="sm" onClick={dismiss} disabled={note.trim().length < 3} data-testid="sanity-dismiss-confirm">Dismiss</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
